<template>
  <b-card no-body class="birthdays-shell-card">
    <b-card-header class="birthdays-page-header">
      <div class="d-flex align-items-center birthdays-title-block">
        <div class="birthday-icon-wrap">
          <feather-icon icon="GiftIcon" size="20" class="text-white" />
        </div>
        <div class="birthdays-heading-text">
          <h4 class="mb-0 birthdays-title">{{ $t("Birthdays") }}</h4>
          <small class="birthdays-subtitle">{{ $t("Students birthdays grouped by class") }}</small>
        </div>
      </div>
      <div class="birthdays-filter-row">
        <div class="birthdays-search-wrap mr-50">
          <feather-icon icon="SearchIcon" size="14" class="birthdays-search-icon" />
          <b-form-input
            v-model="searchQuery"
            size="sm"
            class="birthdays-search-input"
            :placeholder="$t('Search student or class...')"
          />
        </div>
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="birthdays-filter-chip"
          :class="{ 'birthdays-filter-chip--active': activeFilter === tab.key }"
          @click="activeFilter = tab.key"
        >
          {{ $t(tab.label) }}
          <span class="birthdays-filter-chip__count">{{
            counts[tab.key] || 0
          }}</span>
        </button>
      </div>
    </b-card-header>

    <div class="birthdays-columns-wrap">
      <div
        v-if="loading"
        class="d-flex justify-content-center align-items-center py-5"
      >
        <b-spinner variant="primary" type="grow" />
      </div>

      <div
        v-else-if="filteredClassGroups.length === 0"
        class="birthdays-empty-state py-5 my-2"
      >
        <feather-icon
          icon="CalendarIcon"
          size="48"
          class="birthdays-empty-state__icon mb-1"
        />
        <h5 class="font-weight-bolder text-muted mb-50">{{ $t("No records found") }}</h5>
        <p class="birthdays-empty-state__msg mb-0">{{ $t("No birthdays available for the selected filter.") }}</p>
      </div>

      <b-row v-else class="birthdays-columns-row">
        <b-col
          v-for="cls in filteredClassGroups"
          :key="cls.classID"
          cols="12"
          md="6"
          lg="4"
          class="birthdays-col mb-1"
        >
          <div class="birthday-column-card">
            <div class="column-card-header">
              <div class="column-card-header__main">
                <div class="column-icon-wrap">
                  <feather-icon
                    icon="BookOpenIcon"
                    size="16"
                    class="text-primary"
                  />
                </div>
                <div class="column-title-wrap min-w-0">
                  <span class="column-title text-truncate">{{ cls.className }}</span>
                  <!-- <span class="column-count-pill">{{ cls.filteredStudents.length }}</span> -->
                </div>
              </div>
              <span class="column-hint">
                {{ cls.totalStudents != null ? cls.totalStudents : (cls.students || []).length }}
                {{ (cls.totalStudents != null ? cls.totalStudents : (cls.students || []).length) === 1 ? $t("Student") : $t("Students") }}
              </span>
            </div>

            <div class="column-card-body">
              <div
                v-if="cls.filteredStudents.length === 0"
                class="birthdays-empty-state column-inner-fill"
              >
                <feather-icon
                  icon="CalendarIcon"
                  size="40"
                  class="birthdays-empty-state__icon mb-1"
                />
                <p class="birthdays-empty-state__msg mb-0">{{ $t("No birthdays in this class.") }}</p>
              </div>

              <div v-else class="column-scroll-list">
                <div
                  v-for="(item, idx) in cls.filteredStudents"
                  :key="(item.id || idx) + '-' + idx"
                  class="birthday-row"
                  :class="{ 'birthday-row--today': isTodayBirthday(item) }"
                >
                  <div class="birthday-row__inner">
                    <div class="birthday-avatar birthday-avatar--ring">
                      <img
                        v-if="item.picture"
                        :src="getPicturePreview(item.picture)"
                        width="40"
                        height="40"
                        style="border-radius: 50%; object-fit: cover"
                        referrerpolicy="no-referrer"
                        alt=""
                        @error="onPicturePreviewError($event, item.picture)"
                      />
                      <div
                        v-else
                        class="avatar-placeholder-sm d-flex align-items-center justify-content-center rounded-circle"
                        :class="avatarBg(item.type)"
                      >
                        <feather-icon
                          icon="UserIcon"
                          size="16"
                          class="text-white"
                        />
                      </div>
                    </div>

                    <div class="birthday-row__mid">
                      <div class="birthday-name-row">
                        <span class="birthday-name">{{
                          item.name
                        }}{{ item.surname ? " " + item.surname : "" }}</span>
                        <!-- <span
                          class="birthdays-type-pill birthdays-type-pill--student"
                        >
                          {{ $t("Student") }}
                        </span> -->
                      </div>
                      <div class="birthday-meta text-truncate">
                        <span v-if="item.grNo">GR {{ item.grNo }}</span>
                        <span v-if="item.meta_class">
                          <span v-if="item.grNo" class="mx-50 text-muted">·</span
                          >{{ item.meta_class }}</span
                        >
                        <span v-if="item.father_name && !item.meta_class">
                          <span v-if="item.grNo" class="mx-50 text-muted">·</span
                          >S/D/O {{ item.father_name }}</span
                        >
                      </div>
                    </div>

                    <div class="birthday-row__aside">
                      <span
                        class="birthdays-when-pill"
                        :class="relativePillClass(item)"
                      >
                        <feather-icon
                          v-if="isTodayBirthday(item)"
                          icon="StarIcon"
                          size="12"
                          class="mr-25"
                        />
                        {{
                          isTodayBirthday(item)
                            ? $t("Today") + " 🎂"
                            : formatRelativeTime(item)
                        }}
                      </span>
                      <div
                        v-if="item.date_of_birth"
                        class="birthday-dob-line"
                      >
                        <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                        {{ formatDob(item.date_of_birth) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BRow,
  BCol,
  BSpinner,
  BFormInput,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { getMediaPreviewUrl, handleDrivePreviewError } from "@/utils/gdriveUpload";

export default {
  name: "BirthdayClasswise",
  components: {
    BCard,
    BCardHeader,
    BRow,
    BCol,
    BSpinner,
    BFormInput,
  },
  props: {
    campusID: { type: Number, default: null },
  },
  data() {
    return {
      activeFilter: "all",
      searchQuery: "",
      tabs: [
        { key: "all", label: "All" },
        { key: "today", label: "Today" },
        { key: "upcoming", label: "Upcoming" },
        { key: "passed", label: "Passed" },
      ],
      classList: [],
      loading: false,
    };
  },
  computed: {
    allStudentsList() {
      const all = [];
      (this.classList || []).forEach((c) => {
        if (Array.isArray(c.students)) {
          all.push(...c.students);
        }
      });
      return all;
    },
    counts() {
      const all = this.allStudentsList;
      return {
        all: all.length,
        today: all.filter((s) => this.isTodayBirthday(s)).length,
        upcoming: all.filter(
          (s) =>
            (s.tag || "").toLowerCase() === "upcoming" &&
            !this.isTodayBirthday(s)
        ).length,
        passed: all.filter(
          (s) =>
            (s.tag || "").toLowerCase() === "passed" &&
            !this.isTodayBirthday(s)
        ).length,
      };
    },
    filteredClassGroups() {
      if (!this.classList || !this.classList.length) return [];
      const query = (this.searchQuery || "").trim().toLowerCase();

      return this.classList
        .map((cls) => {
          const classMatchesQuery =
            query && (cls.className || "").toLowerCase().includes(query);
          const filteredStudents = (cls.students || []).filter((s) => {
            // Tab filter
            if (this.activeFilter === "today" && !this.isTodayBirthday(s)) {
              return false;
            }
            if (
              this.activeFilter === "upcoming" &&
              ((s.tag || "").toLowerCase() !== "upcoming" ||
                this.isTodayBirthday(s))
            ) {
              return false;
            }
            if (
              this.activeFilter === "passed" &&
              ((s.tag || "").toLowerCase() !== "passed" ||
                this.isTodayBirthday(s))
            ) {
              return false;
            }

            // Search query filter
            if (!query || classMatchesQuery) return true;
            const name = (s.name || "").toLowerCase();
            const surname = (s.surname || "").toLowerCase();
            const father = (s.father_name || "").toLowerCase();
            const gr = String(s.grNo || "").toLowerCase();
            const meta = (s.meta_class || "").toLowerCase();
            return (
              name.includes(query) ||
              surname.includes(query) ||
              father.includes(query) ||
              gr.includes(query) ||
              meta.includes(query)
            );
          });

          return {
            ...cls,
            filteredStudents,
          };
        })
        .filter((cls) => {
          if (!this.searchQuery && this.activeFilter === "all") return true;
          return (
            cls.filteredStudents.length > 0 ||
            (this.searchQuery &&
              (cls.className || "")
                .toLowerCase()
                .includes(this.searchQuery.trim().toLowerCase()))
          );
        });
    },
  },
  watch: {
    campusID() {
      this.loadBirthdayListClasswise();
    },
  },
  created() {
    this.loadBirthdayListClasswise();
  },
  methods: {
    ...mapActions({ get: "get" }),

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    onPicturePreviewError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },

    formatDob(val) {
      if (!val) return "";
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    avatarBg(type) {
      if (type === "staff") return "bg-gradient-warning";
      if (type === "donor") return "bg-gradient-danger";
      return "bg-gradient-primary";
    },

    isTodayBirthday(item) {
      if (!item) return false;
      const tag = (item.tag || "").toLowerCase();
      return item.daysUntil === 0 || tag === "today" || item.daysUntil === "today";
    },

    formatRelativeTime(item) {
      if (!item) return "—";
      const tag = (item.tag || "").toLowerCase();
      const days = item.daysUntil;

      if (this.isTodayBirthday(item)) {
        return "Today";
      }
      if (typeof days === "number") {
        if (tag === "passed") {
          const count = Math.abs(days);
          if (count === 1) return "Yesterday";
          return `${count} days ago`;
        }
        if (tag === "upcoming" || days > 0) {
          if (days === 1) return "Tomorrow";
          return `In ${days} days`;
        }
      }
      if (typeof days === "string") {
        return days;
      }
      return "—";
    },

    relativePillClass(item) {
      if (!item) return "birthdays-when-pill--muted";
      const tag = (item.tag || "").toLowerCase();
      const days = item.daysUntil;

      if (this.isTodayBirthday(item)) {
        return "birthdays-when-pill--today";
      }
      if (tag === "passed" || (typeof days === "number" && days < 0)) {
        return "birthdays-when-pill--past";
      }
      if (tag === "upcoming" || (typeof days === "number" && days > 0)) {
        return "birthdays-when-pill--soon";
      }
      return "birthdays-when-pill--soon";
    },

    async loadBirthdayListClasswise() {
      this.loading = true;
      try {
        const db = this.$store.state.userData.db;
        const cID = this.campusID || this.$store.state.userData.cId;
        const url = `${this.$store.state.domain}Users/GetBirthdayListClasswise?db=${encodeURIComponent(
          db
        )}&cID=${cID}`;
        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        if (Array.isArray(res)) {
          this.classList = res;
        } else if (res && Array.isArray(res.data)) {
          this.classList = res.data;
        } else {
          this.classList = [];
        }
      } catch {
        this.classList = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.min-w-0 {
  min-width: 0;
}

.object-fit-cover {
  object-fit: cover;
}

/* Shell card — aligned with marksEntryStudent student-detail-card */
::v-deep .birthdays-shell-card.card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid #e0d8ff;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
}

.birthdays-page-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem 1.25rem;
  padding: 1.15rem 1.35rem 1rem !important;
  background: transparent !important;
  border-bottom: 2px solid #f0edff !important;
}

.birthdays-title-block {
  gap: 0.85rem;
}

.birthday-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  box-shadow: 0 6px 18px rgba(124, 93, 250, 0.35);
}

.birthdays-heading-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.birthdays-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.35px;
}

.birthdays-subtitle {
  font-size: 0.82rem;
  font-weight: 600;
  color: #8a86b7;
  letter-spacing: 0.02em;
}

.birthdays-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.birthdays-search-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.birthdays-search-icon {
  position: absolute;
  left: 0.75rem;
  color: #9d7aff;
  pointer-events: none;
  z-index: 2;
}

.birthdays-search-input {
  border-radius: 9999px !important;
  padding-left: 2rem !important;
  min-height: 2.375rem;
  border: 2px solid #e0d8ff !important;
  background: #fff !important;
  font-size: 0.8125rem !important;
  font-weight: 600;
  width: 210px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);
  transition: all 0.25s ease;

  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.25) !important;
    width: 250px;
  }
}

.birthdays-filter-chip {
  appearance: none;
  border: 2px solid #e0d8ff;
  background: #fff;
  color: #63607d;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0 1rem;
  min-height: 2.375rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  line-height: 1.2;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.06);

  &:hover {
    border-color: #cbb9ff;
    color: #4c3d91;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(124, 93, 250, 0.14);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.25);
  }
}

.birthdays-filter-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.4rem;
  font-size: 0.68rem;
  font-weight: 800;
  border-radius: 9999px;
  background: #f3f0ff;
  color: #7c5dfa;
  border: 1px solid #e0d8ff;
}

.birthdays-filter-chip--active {
  border-color: transparent;
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
  color: #fff;
  box-shadow: 0 6px 22px rgba(124, 93, 250, 0.42);
  transform: translateY(-1px);

  &:hover {
    border-color: transparent;
    color: #fff;
    box-shadow: 0 8px 26px rgba(124, 93, 250, 0.48);
  }

  .birthdays-filter-chip__count {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.35);
  }
}

.birthdays-columns-wrap {
  padding: 1rem 1.15rem 1.25rem;
}

.birthdays-columns-row {
  margin-left: -0.45rem;
  margin-right: -0.45rem;
}

.birthdays-col {
  padding-left: 0.45rem;
  padding-right: 0.45rem;
}

.birthday-column-card {
  display: flex;
  flex-direction: column;
  height: 360px;
  border-radius: 16px;
  border: 1.5px solid #f0edff;
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  box-shadow: 0 6px 22px rgba(99, 102, 241, 0.1);
  overflow: hidden;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    border-color: #d4c7ff;
    box-shadow: 0 10px 28px rgba(99, 102, 241, 0.14);
  }
}

.column-card-header {
  flex-shrink: 0;
  padding: 0.65rem 0.85rem;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  border-bottom: 2px solid #f0edff;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
}

.column-card-header__main {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  flex: 1;
}

.column-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 2px solid #e0d8ff;
  box-shadow: 0 4px 12px rgba(124, 93, 250, 0.1);
  color: #7c5dfa;
}

.column-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  flex: 1;
}

.column-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.2px;
}

.column-count-pill {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 800;
  min-width: 1.65rem;
  height: 1.65rem;
  padding: 0 0.45rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: linear-gradient(135deg, #e0f2f7 0%, #d1e7f0 100%);
  color: #0369a1;
  border: 1px solid #bae6fd;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
}

.column-hint {
  font-size: 0.72rem;
  font-weight: 600;
  color: #9ca3af;
  max-width: 100%;
  text-align: right;
  white-space: nowrap;
}

.column-card-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.column-inner-fill {
  flex: 1;
  min-height: 0;
}

.column-scroll-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.35rem 0.45rem 0.55rem;
  scrollbar-width: thin;
  scrollbar-color: #c4b5fd transparent;
}

.column-scroll-list::-webkit-scrollbar {
  width: 3px;
}

.column-scroll-list::-webkit-scrollbar-track {
  background: transparent;
}

.column-scroll-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: linear-gradient(180deg, #cbb9ff 0%, #a78bfa 100%);
  border: 1px solid transparent;
  background-clip: padding-box;
}

.column-scroll-list::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #b8a3fc 0%, #8b5cf6 100%);
  background-clip: padding-box;
}

.birthdays-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.25rem 1rem;
  margin: 0.5rem;
  border-radius: 14px;
  border: 2px dashed #e5e7eb;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
}

.birthdays-empty-state__icon {
  color: #c4b5fd;
  opacity: 0.85;
}

.birthdays-empty-state__msg {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
  max-width: 220px;
  line-height: 1.45;
}

.birthday-row {
  padding: 0.25rem 0;
}

.birthday-row__inner {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.7rem;
  border-radius: 12px;
  border: 1px solid transparent;
  background: transparent;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    background: linear-gradient(90deg, #faf9ff 0%, #ffffff 100%);
    border-color: #ece9fb;
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.07);
  }
}

.birthday-row--today .birthday-row__inner {
  background: linear-gradient(90deg, #ecfdf5 0%, #ffffff 100%);
  border-color: #a7f3d0;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.12);

  &:hover {
    background: linear-gradient(90deg, #d1fae5 0%, #fafffd 100%);
    border-color: #6ee7b7;
  }
}

.birthday-avatar {
  flex-shrink: 0;
}

.birthday-avatar--ring {
  padding: 2px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0d8ff 0%, #f0edff 100%);
}

.avatar-placeholder-sm {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.birthday-row__mid {
  flex: 1;
  min-width: 0;
}

.birthday-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.45rem;
}

.birthday-name {
  font-size: 0.875rem;
  font-weight: 800;
  color: #2d1b69;
  letter-spacing: -0.15px;
}

.birthdays-type-pill {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: capitalize;
  letter-spacing: 0.06em;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  border: 1px solid;
  line-height: 1.2;
}

.birthdays-type-pill--student {
  color: #5b21b6;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  border-color: #c4b5fd;
  box-shadow: 0 2px 6px rgba(91, 33, 182, 0.12);
}

.birthday-meta {
  margin-top: 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
}

.birthday-row__aside {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  max-width: 108px;
}

.birthdays-when-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.28rem 0.5rem;
  border-radius: 999px;
  text-align: center;
  line-height: 1.25;
  white-space: normal;
  border: 1px solid transparent;
}

.birthdays-when-pill--muted {
  color: #6b7280;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.birthdays-when-pill--today {
  color: #fff;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border: none;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
}

.birthdays-when-pill--soon {
  color: #5b21b6;
  background: linear-gradient(135deg, #ede9fe 0%, #e9d5ff 100%);
  border-color: #c4b5fd;
  box-shadow: 0 2px 8px rgba(124, 93, 250, 0.18);
}

.birthdays-when-pill--past {
  color: #4b5563;
  background: #f9fafb;
  border-color: #e5e7eb;
}

.birthday-dob-line {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 600;
  color: #9ca3af;
}
</style>
